export const IDENTIFIER_TYPE = {
  ID: 'id',
  NAME: 'name',
  EMAIL: 'email',
};

export const SSO_PROVIDER = {
  MICROSOFT: 'microsoft',
  GOOGLE: 'google',
  OKTA: 'okta',
};

// Providers offered when creating a config. Okta is absent by choice —
// it is creatable through the API and existing configs must stay
// editable here, but it is not part of this release's UI scope.
export const SUPPORTED_PROVIDERS = [SSO_PROVIDER.MICROSOFT, SSO_PROVIDER.GOOGLE];

// Credential fields are provider-specific: Microsoft Entra is scoped to a
// tenant, while Google serves every Workspace from one endpoint and reads
// group membership from the Admin SDK using a service account instead.
export const PROVIDER_CREDENTIAL_FIELDS = {
  [SSO_PROVIDER.MICROSOFT]: ['client_id', 'client_secret', 'tenant_id'],
  [SSO_PROVIDER.GOOGLE]: [
    'client_id',
    'client_secret',
    'service_account_key',
    'workspace_admin_email',
  ],
  // Okta shares Microsoft's shape; its tenant is the org subdomain.
  [SSO_PROVIDER.OKTA]: ['client_id', 'client_secret', 'tenant_id'],
};

// Label and helper text for the tenant field, which means something
// different per provider.
export const PROVIDER_TENANT_FIELD = {
  [SSO_PROVIDER.MICROSOFT]: {
    label: 'Tenant ID',
    helperText: 'Microsoft Entra directory (tenant) ID',
  },
  [SSO_PROVIDER.OKTA]: {
    label: 'Okta Org',
    helperText: 'Okta org subdomain, e.g. "acme" for acme.okta.com',
  },
};

// Email identifiers exist only for Google: a Microsoft ID token's groups
// claim carries IDs or display names, never an email address.
export const PROVIDER_IDENTIFIER_TYPES = {
  [SSO_PROVIDER.MICROSOFT]: [IDENTIFIER_TYPE.ID, IDENTIFIER_TYPE.NAME],
  [SSO_PROVIDER.GOOGLE]: [
    IDENTIFIER_TYPE.ID,
    IDENTIFIER_TYPE.NAME,
    IDENTIFIER_TYPE.EMAIL,
  ],
  [SSO_PROVIDER.OKTA]: [IDENTIFIER_TYPE.ID, IDENTIFIER_TYPE.NAME],
};

// Google groups have no admin-visible ID: a Workspace admin can supply a
// group email, but the opaque ID has to be dug out of the Admin SDK. The
// email also survives a rename, so it is the sane default there.
export const PROVIDER_DEFAULT_IDENTIFIER_TYPE = {
  [SSO_PROVIDER.MICROSOFT]: IDENTIFIER_TYPE.ID,
  [SSO_PROVIDER.GOOGLE]: IDENTIFIER_TYPE.EMAIL,
  [SSO_PROVIDER.OKTA]: IDENTIFIER_TYPE.ID,
};

export const getDefaultIdentifierType = (provider) =>
  PROVIDER_DEFAULT_IDENTIFIER_TYPE[provider] || IDENTIFIER_TYPE.ID;

export const providerUsesField = (provider, field) =>
  (PROVIDER_CREDENTIAL_FIELDS[provider] || []).includes(field);

export const IDENTIFIER_TYPE_LABEL = {
  [IDENTIFIER_TYPE.ID]: 'ID',
  [IDENTIFIER_TYPE.NAME]: 'Name',
  [IDENTIFIER_TYPE.EMAIL]: 'Group Email',
};

export const GROUP_IDENTIFIER_LABEL = {
  [IDENTIFIER_TYPE.ID]: 'IdP Group ID',
  [IDENTIFIER_TYPE.NAME]: 'IdP Group Name',
  [IDENTIFIER_TYPE.EMAIL]: 'IdP Group Email',
};

// Which mapping field the identifier type matches against. Email is stored
// in idp_group_id because that is the stable identifier column.
export const getGroupKeyField = (identifierType) =>
  identifierType === IDENTIFIER_TYPE.NAME ? 'idp_group_name' : 'idp_group_id';

export const capitalizeProvider = (provider) =>
  provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : '-';
